import {
  EditorContent,
  Extension,
  generateJSON,
  useEditor,
} from "@tiptap/react";
import React, { forwardRef, useImperativeHandle } from "react";
import { Plugin, PluginKey } from "prosemirror-state";
import StarterKit from "@tiptap/starter-kit";
import { turndownService } from "@/utils/markdownHelper";
import CharacterCount from "@tiptap/extension-character-count";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";

const EventHandler = Extension.create({
  name: "eventHandler",

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handlePaste(_, event) {
            const { clipboardData } = event;
            if (!clipboardData) {
              return false;
            }

            const text = clipboardData.getData("text/plain");
            const html = clipboardData.getData("text/html");
            if (html.length > 0) {
              return false;
            }
            const content = text
              .split("\n")
              .map((line) => `<p>${line}</p>`)
              .join("");
            editor.commands.insertContent(
              generateJSON(content, [EventHandler, StarterKit]),
              {
                parseOptions: {
                  preserveWhitespace: "full",
                },
              }
            );

            return true;
          },
        },
      }),
    ];
  },
});

interface MarkdownEditorProps {
  value?: string;
  onChange?: (markdown: string, html: string, len: number) => void;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
}

const MarkdownEditor = forwardRef((props: MarkdownEditorProps, ref) => {
  const { value, onChange, placeholder, onBlur, onFocus, autoFocus } = props;

  const editor = useEditor({
    immediatelyRender: false,
    // autofocus: autoFocus,
    extensions: [
      StarterKit,
      Typography,
      EventHandler,
      Placeholder.configure({
        placeholder:
          placeholder || "粘贴您的笔记、大纲或任何文本内容可直接生成PPT",
      }),
      CharacterCount.configure({
        limit: 0,
      }),
    ],
    onTransaction(props) {
      const { editor } = props;
      const html = editor.getHTML();
      const markdown = turndownService.turndown(html);
      onChange &&
        onChange(
          markdown,
          html,
          editor?.storage.characterCount.characters() || 0
        );
    },
    onBlur: () => {
      onBlur && onBlur();
    },
    onFocus: () => {
      onFocus && onFocus();
    },
    content: value,
  });

  useImperativeHandle(ref, () => ({
    editor: () => editor,
  }));

  return <EditorContent editor={editor} />;
});

MarkdownEditor.displayName = "MarkdownEditor";

export default React.memo(MarkdownEditor);
